/**
 * Call tracking: the reasons a call is sorted into, what the moods mean, and re-reading the
 * calls already sorted.
 *
 * THE RE-SORT BANNER LEADS, WHEN THERE IS ONE, as Settings v2 draws it - but it is only ever
 * about what is SAVED. `agent.reread` is the Worker's answer to "has a saved change to the
 * list left recent calls filed the old way", so a list edited and not yet saved draws nothing
 * here until the save lands. The prototype's range select ("last 30 / 90 days / all") is not
 * here: the Worker re-reads a fixed 30 days (`REREAD_DAYS`) and caps it, and a select that
 * offered 90 would be a promise it cannot keep.
 *
 * IT IS THE ONE THING ON SETTINGS THAT SPENDS MONEY, which is why it stays a button of its own
 * and not a consequence of saving: re-reading rewrites each call's summary, reasons and
 * questions, and the sentence beside the button says so before it is pressed. Pressing it
 * queues and returns; the pane polls every thirty seconds while calls are outstanding.
 */

import { useEffect, useState } from "react";
import { ApiError, post, type SettingsPage } from "../../../api";
import { Categories } from "../../Categories";
import { Sentiments } from "../../Sentiments";
import { Button } from "../../../ui";
import { useDraft } from "../draft";

export function TrackingPane({ agent, onQueued }: { agent: NonNullable<SettingsPage["agent"]>; onQueued: () => void }) {
  const { state, set } = useDraft();
  const taxonomy = state.draft.taxonomy;
  return (
    <>
      <RereadBanner agent={agent} onQueued={onQueued} />
      <Categories taxonomy={taxonomy} saved={state.baseline.taxonomy} onChange={(next) => set("taxonomy", next)} />
      <Sentiments value={state.draft.sentiments} onChange={(next) => set("sentiments", next)} />
    </>
  );
}

function RereadBanner({ agent, onQueued }: { agent: NonNullable<SettingsPage["agent"]>; onQueued: () => void }) {
  const reread = agent.reread;
  const [queueing, setQueueing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = reread?.pending ?? 0;

  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(onQueued, 30_000);
    return () => clearInterval(timer);
  }, [pending, onQueued]);

  if (!reread || (reread.stale === 0 && pending === 0)) return null;

  async function start() {
    setQueueing(true);
    setError(null);
    try {
      await post("/v1/settings/agent/reread", { agent: agent.id });
      onQueued();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start that");
    } finally {
      setQueueing(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-md border border-accent-line bg-accent-soft px-4.5 py-3.5">
      <span className="min-w-0 flex-1 text-sm text-ink">
        {pending > 0
          ? `Re-sorting ${pending} call${pending === 1 ? "" : "s"}. Older calls show the new reasons within a few minutes.`
          : "Your reasons for calling changed. New calls use them straight away; older calls keep their old reasons until you re-sort. Re-sorting rewrites each call's summary, reasons and questions."}
      </span>
      {reread.stale > 0 && (
        <Button type="button" className="border-accent bg-accent text-accent-ink hover:border-accent-hover hover:bg-accent-hover" disabled={queueing} onClick={() => void start()}>
          {queueing ? "Starting…" : `Re-sort the last ${reread.days} days (${reread.stale} call${reread.stale === 1 ? "" : "s"})`}
        </Button>
      )}
      {error && <span className="basis-full text-xs text-alert">{error}</span>}
    </div>
  );
}
